import {
    ButtonGradientPrimary,
    ButtonSecondary,
    ConfirmDialog,
    MultilineInput,
    TextInput,
} from '@amelio/ui-library';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { MeetingPlanAgendaSection } from './MeetingPlanAgendaSection';
import { MeetingPlanConversationTipFields } from './MeetingPlanConversationTipFields';
import { PLAN_FIELD_VARIANT } from './meetingPlanFieldVariant';
import { MeetingPlanIdentitySection } from './MeetingPlanIdentitySection';
import { MeetingPlanSectionCard } from './MeetingPlanSectionCard';
import { MeetingPlanTextListFields } from './MeetingPlanTextListFields';
import {
    INVITATION_TEXT_MAX_LENGTH,
    MeetingPlanTemplateFormFields,
    OPENING_STATEMENT_TEXT_MAX_LENGTH,
    OPENING_STATEMENT_TITLE_MAX_LENGTH,
    meetingPlanTemplateSchema,
    type MeetingPlanTemplateFormData,
} from './validation';

import type { ThematicMeetingTopic } from '@/types/thematic-meetings';
import type { ContentLanguage } from '@/utils/contentLanguage';
import type { FieldErrors } from 'react-hook-form';

import { FormField } from '@/components/FormField/FormField';
import { useAmelioTranslations } from '@/hooks';
import { validationMessageKey } from '@/utils/validationMessage';

type MeetingPlanTemplateFormProps = {
    defaultValues: MeetingPlanTemplateFormData;
    language: ContentLanguage;
    topic: ThematicMeetingTopic;
    isSaving: boolean;
    onSubmit: (data: MeetingPlanTemplateFormData) => void;
    onCancel: () => void;
};

/**
 * The first thing the schema refuses about the agenda. The subjects are written in the shared agenda
 * editor, which has no field to hang a message under, so the section shows one — a length past the
 * bound being the only refusal its own controls let through.
 */
const firstAgendaIssue = (
    errors: FieldErrors<MeetingPlanTemplateFormData>['agendaItems'],
): string | undefined => {
    if (!errors) {
        return undefined;
    }

    if (typeof errors.message === 'string') {
        return errors.message;
    }

    return Array.from(errors as ArrayLike<Record<string, { message?: string }> | undefined>)
        .flatMap((subject) => Object.values(subject ?? {}))
        .find((issue) => typeof issue?.message === 'string')?.message;
};

export const MeetingPlanTemplateForm = ({
    defaultValues,
    language,
    topic,
    isSaving,
    onSubmit,
    onCancel,
}: MeetingPlanTemplateFormProps) => {
    const { t } = useAmelioTranslations();
    const errorMessageOf = (message: string | undefined) =>
        message ? t(validationMessageKey(message)) : undefined;
    const [isDiscardAsked, setIsDiscardAsked] = useState(false);
    const {
        control,
        handleSubmit,
        formState: { isDirty, errors },
    } = useForm<MeetingPlanTemplateFormData>({
        resolver: zodResolver(meetingPlanTemplateSchema),
        defaultValues,
    });

    // A plan is twenty-nine fields, and "Annuler" sits next to "Enregistrer": a misclick used to
    // throw a rewriting away without a word, and there is no version history to get it back from.
    // A form nobody has touched has nothing to lose and leaves at once.
    const askBeforeDiscarding = () => (isDirty ? setIsDiscardAsked(true) : onCancel());

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            className={'flex flex-col gap-6'}
            data-testid={'meeting-plan-template-form'}
        >
            <MeetingPlanIdentitySection
                control={control}
                topic={topic}
            />

            <MeetingPlanSectionCard
                title={t('templateManagement.meetingPlanEditor.openingStatementHeading')}
                dataTestId={'meeting-plan-template-opening'}
            >
                <div className={'flex flex-col gap-4'}>
                    <Controller
                        control={control}
                        name={MeetingPlanTemplateFormFields.openingStatementTitle}
                        render={({ field, fieldState }) => (
                            <FormField
                                label={t(
                                    'templateManagement.meetingPlanEditor.openingStatementTitleLabel',
                                )}
                                errorMessage={errorMessageOf(fieldState.error?.message)}
                            >
                                <TextInput
                                    variant={PLAN_FIELD_VARIANT}
                                    value={field.value}
                                    onChange={field.onChange}
                                    maxLength={OPENING_STATEMENT_TITLE_MAX_LENGTH}
                                    dataTestId={'meeting-plan-template-opening-title'}
                                />
                            </FormField>
                        )}
                    />

                    <Controller
                        control={control}
                        name={MeetingPlanTemplateFormFields.openingStatementText}
                        render={({ field, fieldState }) => (
                            <FormField
                                label={t(
                                    'templateManagement.meetingPlanEditor.openingStatementTextLabel',
                                )}
                                required={true}
                                errorMessage={errorMessageOf(fieldState.error?.message)}
                            >
                                <MultilineInput
                                    variant={PLAN_FIELD_VARIANT}
                                    value={field.value}
                                    onChange={field.onChange}
                                    maxLength={OPENING_STATEMENT_TEXT_MAX_LENGTH}
                                    rows={4}
                                    dataTestId={'meeting-plan-template-opening-text'}
                                />
                            </FormField>
                        )}
                    />
                </div>
            </MeetingPlanSectionCard>

            <MeetingPlanTextListFields
                control={control}
                name={MeetingPlanTemplateFormFields.preparationQuestions}
                heading={t('templateManagement.meetingPlanEditor.preparationHeading')}
                description={t('templateManagement.meetingPlanEditor.preparationDescription')}
                entryLabel={t('templateManagement.meetingPlanEditor.preparationEntryLabel')}
                addLabel={t('templateManagement.meetingPlanEditor.addPreparationQuestion')}
                numbered
                dataTestId={'meeting-plan-template-preparation'}
            />

            {/* The shared agenda owns the subjects once it has them, so it is seeded from the plan
                as loaded and reports back — never handed `field.value`, which is its own echo. */}
            <Controller
                control={control}
                name={MeetingPlanTemplateFormFields.agendaItems}
                render={({ field }) => (
                    <MeetingPlanAgendaSection
                        value={defaultValues.agendaItems}
                        onChange={field.onChange}
                        language={language}
                        errorMessage={errorMessageOf(firstAgendaIssue(errors.agendaItems))}
                    />
                )}
            />

            <MeetingPlanTextListFields
                control={control}
                name={MeetingPlanTemplateFormFields.tips}
                heading={t('templateManagement.meetingPlanEditor.tipsHeading')}
                entryLabel={t('templateManagement.meetingPlanEditor.tipEntryLabel')}
                addLabel={t('templateManagement.meetingPlanEditor.addTip')}
                dataTestId={'meeting-plan-template-tips'}
            />

            <MeetingPlanTextListFields
                control={control}
                name={MeetingPlanTemplateFormFields.observations}
                heading={t('templateManagement.meetingPlanEditor.observationsHeading')}
                entryLabel={t('templateManagement.meetingPlanEditor.observationEntryLabel')}
                addLabel={t('templateManagement.meetingPlanEditor.addObservation')}
                dataTestId={'meeting-plan-template-observations'}
            />

            <MeetingPlanConversationTipFields
                control={control}
                dataTestId={'meeting-plan-template-conversation-tip'}
            />

            <MeetingPlanSectionCard
                title={t('templateManagement.meetingPlanEditor.invitationHeading')}
                dataTestId={'meeting-plan-template-invitation-section'}
            >
                <Controller
                    control={control}
                    name={MeetingPlanTemplateFormFields.invitationText}
                    render={({ field, fieldState }) => (
                        <FormField
                            label={t('templateManagement.meetingPlanEditor.invitationTextLabel')}
                            errorMessage={errorMessageOf(fieldState.error?.message)}
                        >
                            <MultilineInput
                                variant={PLAN_FIELD_VARIANT}
                                value={field.value}
                                onChange={field.onChange}
                                maxLength={INVITATION_TEXT_MAX_LENGTH}
                                rows={4}
                                dataTestId={'meeting-plan-template-invitation'}
                            />
                        </FormField>
                    )}
                />
            </MeetingPlanSectionCard>

            <div className={'flex justify-end gap-3'}>
                <ButtonSecondary
                    onClick={askBeforeDiscarding}
                    dataTestId={'meeting-plan-template-cancel'}
                >
                    {t('common.cancel')}
                </ButtonSecondary>

                <ButtonGradientPrimary
                    type={'submit'}
                    disabled={isSaving}
                    dataTestId={'meeting-plan-template-save'}
                >
                    {t('common.save')}
                </ButtonGradientPrimary>
            </div>

            {/* Written next to the button it guards; the library's dialog portals out of the form. */}
            <ConfirmDialog
                isOpen={isDiscardAsked}
                title={t('templateManagement.meetingPlanEditor.discardTitle')}
                message={t('templateManagement.meetingPlanEditor.discardMessage')}
                confirmLabel={t('templateManagement.meetingPlanEditor.discardConfirm')}
                cancelLabel={t('templateManagement.meetingPlanEditor.discardKeepEditing')}
                confirmTone={'danger'}
                onConfirm={onCancel}
                onCancel={() => setIsDiscardAsked(false)}
                dataTestId={'meeting-plan-template-discard-dialog'}
            />
        </form>
    );
};
